import type { PlanCapitalAllocation } from './StockPickTypes';

/** Types for plan stock execution (purchases + partial sells). */

export interface IndianMarketCharges {
  stt: number;
  brokerage: number;
  gstOnBrokerage: number;
  exchangeTxnCharges: number;
  sebiTurnoverFees: number;
  stampDuty: number;
}

export interface PlanPurchase {
  /** Absent when purchase is synthesized from legacy plan result (not yet in DB). */
  id?: string;
  tradeDate: string;
  quantity: number;
  pricePerShare: number;
  charges: IndianMarketCharges;
  comments?: string;
  partnerAllocations?: PlanCapitalAllocation[];
}

export interface PlanSell {
  id: string;
  tradeDate: string;
  quantity: number;
  pricePerShare: number;
  charges: IndianMarketCharges;
  /** Derived from FIFO lots vs sell date */
  capitalGainsType: 'STCG' | 'LTCG';
  comments?: string;
}

export interface PlanExecutionData {
  /** All buy lots on this plan (ordered by trade date). */
  purchases?: PlanPurchase[];
  /** @deprecated Use purchases — kept for backward compatibility */
  purchase?: PlanPurchase;
  sells: PlanSell[];
  /** Aggregated partner amounts across all purchases. */
  partnerAllocations?: PlanCapitalAllocation[];
  /** Sum of gross buy + charges for all purchases. */
  totalPurchaseCost?: number;
}

export function getPlanPurchases(data: PlanExecutionData): PlanPurchase[] {
  if (data.purchases?.length) return data.purchases;
  if (data.purchase) return [data.purchase];
  return [];
}

export function totalSharesBought(data: PlanExecutionData): number {
  return getPlanPurchases(data).reduce((s, p) => s + p.quantity, 0);
}

export type PlanTimelineEventType = 'PURCHASE' | 'SELL' | 'NOTE';

export interface PlanTimelineEvent {
  id: string;
  type: PlanTimelineEventType;
  date: string;
  title: string;
  subtitle?: string;
  body?: string;
  metrics?: { label: string; value: string }[];
}

export interface PlanExecutionReport {
  sharesBought: number;
  avgPurchasePrice: number;
  totalPurchaseValue: number;
  sharesSold: number;
  avgSellPrice: number;
  totalSellProceeds: number;
  sharesRemaining: number;
  totalCharges: IndianMarketCharges;
  realizedGainBeforeTax: number;
  estimatedStcgTax: number;
  estimatedLtcgTax: number;
  netRealizedGain: number;
}

export function emptyIndianCharges(): IndianMarketCharges {
  return {
    stt: 0,
    brokerage: 0,
    gstOnBrokerage: 0,
    exchangeTxnCharges: 0,
    sebiTurnoverFees: 0,
    stampDuty: 0,
  };
}

export function emptyPurchaseDraft(): Omit<PlanPurchase, 'id'> {
  return {
    tradeDate: new Date().toISOString().slice(0, 10),
    quantity: 0,
    pricePerShare: 0,
    charges: emptyIndianCharges(),
    comments: '',
  };
}

export function emptySellDraft(): Omit<PlanSell, 'id' | 'capitalGainsType'> {
  return {
    tradeDate: new Date().toISOString().slice(0, 10),
    quantity: 0,
    pricePerShare: 0,
    charges: emptyIndianCharges(),
    comments: '',
  };
}
